import { observable } from '@legendapp/state';
import { syncedSupabase, configureSyncedSupabase } from '@legendapp/state/sync-plugins/supabase';
import { ObservablePersistLocalStorage } from '@legendapp/state/persist-plugins/local-storage';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabaseClient';
import { createSyncCreate, createSyncUpdate, defaultSyncConfig } from './syncHelpers';
import { auth$ } from './auth$';
import { trackEvent, setOnceUserProperties, incrementUserProperties } from '../lib/analytics';
import { Capacitor } from '@capacitor/core';

/** Detect source platform for internal analytics */
const getSource = () => Capacitor.isNativePlatform() ? 'android' : 'web';

// Configure syncedSupabase globally with ID generation and sync settings
configureSyncedSupabase({
	generateId: () => uuidv4(),
	// Map to Supabase column names
	fieldCreatedAt: 'created_at',
	fieldUpdatedAt: 'updated_at',
	fieldDeleted: 'deleted',
	// Use incremental mode now that we have a boolean deleted field
	changesSince: 'last-sync',
});

// Notes store with Supabase sync (local-first)
// Using syncedSupabase for automatic sync with local persistence
// NOTE: RLS handles permissions - user can only see own notes
export const notes$ = observable(syncedSupabase({
	supabase,
	collection: 'notes',
	// RLS handles the filtering - user sees only own notes
	// Sync ALL notes (including deleted) for trash view
	// Filtering of deleted/archived notes happens at UI level
	filter: (select) =>
		select
			.order('updated_at', { ascending: false }),
	// Custom create/update with error handling
	create: createSyncCreate('notes'),
	update: createSyncUpdate('notes'),
	// Use default sync config but WITHOUT fieldDeleted
	// We manage 'deleted' as a regular boolean for trash view functionality
	// (fieldDeleted would cause Legend-State to remove deleted records from local state)
	...defaultSyncConfig,
	fieldDeleted: undefined, // Override to keep deleted notes in local state for trash view
	// IMPORTANT: Wait for auth before syncing
	// This prevents sync from failing when user logs in on a new device
	// Without this, sync tries to fetch before auth is ready and RLS blocks it
	waitFor: () => auth$.isAuthenticated.get(),
	// Data shape: object keyed by id (default, but explicit for clarity)
	as: 'object',
	// Initial empty object to prevent undefined issues
	initial: {},
	// Persist data locally
	persist: {
		name: 'promptnote-notes',
		plugin: ObservablePersistLocalStorage,
		retrySync: true,
	},
}));

// Helper: Get notes as array (excludes deleted, includes archived)
export const getNotesArray = () => {
	const notesObj = notes$.get();
	return Object.values(notesObj)
		.filter(
			note => note && !(note.deleted || note.deleted_at),
		)
		.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
};

// Helper: Get active notes (not archived, not deleted)
export const getActiveNotes = () => {
	return getNotesArray().filter(note => !note.archived);
};

// Helper: Get archived notes (archived but not deleted)
export const getArchivedNotes = () => {
	return getNotesArray().filter(note => note.archived);
};

// Helper: Get deleted notes (for trash view)
export const getDeletedNotes = () => {
	const notesObj = notes$.get();
	return Object.values(notesObj)
		.filter(note => note && (note.deleted || note.deleted_at))
		.sort((a, b) => new Date(b.deleted_at) - new Date(a.deleted_at));
};

// Helper: Get notes by tag
export const getNotesByTag = (tag) => {
  if (!tag) return getNotesArray();
  return getNotesArray().filter(note => note.tags?.includes(tag));
};

// ── Smart Tags ───────────────────────────────────────────────
export const SMART_TAG_PREFIX = '✦';

export const isSmartTag = (tag) => tag && tag.startsWith(SMART_TAG_PREFIX);

/** Replace all smart tags on a note with new ones */
export const setSmartTags = (noteId, smartTags) => {
  const note = notes$[noteId].get();
  if (!note) return;

  const userTags = (note.tags || []).filter(t => !isSmartTag(t));
  notes$[noteId].tags.set([...userTags, ...smartTags]);
  // Don't bump updated_at — smart tags are system-managed
};

/** Get smart tag counts for sidebar */
export const getSmartTagTree = () => {
  const notes = getNotesArray();
  const tagCounts = {};

  notes.forEach(note => {
    if (note.archived) return;
    (note.tags || []).forEach(tag => {
      if (isSmartTag(tag)) {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      }
    });
  });

  return Object.entries(tagCounts)
    .sort(([, a], [, b]) => b - a)
    .map(([tag, count]) => ({
      tag,
      label: tag.slice(SMART_TAG_PREFIX.length),
      count,
    }));
};

// Helper: Get all tags
export const getAllTags = () => {
  const notes = getNotesArray();
  const tagSet = new Set();
  notes.forEach(note => {
    (note.tags || []).forEach(tag => tagSet.add(tag));
  });
  return Array.from(tagSet).sort();
};

// Helper: Build tag tree for hierarchical display with / delimiter
// Returns tree with active and archived counts separated
export const getTagTree = () => {
  const notes = getNotesArray();
  const tagCountsActive = {};
  const tagCountsArchived = {};

  // Count occurrences of each tag (separate active vs archived, exclude smart tags)
  notes.forEach(note => {
    const isArchived = note.archived;
    (note.tags || []).forEach(tag => {
      if (isSmartTag(tag)) return; // Smart tags shown in separate section
      if (isArchived) {
        tagCountsArchived[tag] = (tagCountsArchived[tag] || 0) + 1;
      } else {
        tagCountsActive[tag] = (tagCountsActive[tag] || 0) + 1;
      }
    });
  });

  // Get all unique tags
  const allTags = new Set([...Object.keys(tagCountsActive), ...Object.keys(tagCountsArchived)]);
  const tree = {};

  allTags.forEach(tag => {
    const parts = tag.split('/'); // Changed from ':' to '/'
    let currentLevel = tree;
    let currentPath = '';

    parts.forEach((part, index) => {
      currentPath = currentPath ? `${currentPath}/${part}` : part;

      if (!currentLevel[part]) {
        currentLevel[part] = {
          name: part,
          fullPath: currentPath,
          count: 0, // Active count
          archivedCount: 0, // Archived count
          children: {},
          isVirtual: true, // Will be set to false if it's a real tag
        };
      }

      // Add counts to leaf nodes
      if (index === parts.length - 1) {
        currentLevel[part].count = tagCountsActive[tag] || 0;
        currentLevel[part].archivedCount = tagCountsArchived[tag] || 0;
        currentLevel[part].isVirtual = false; // Real tag (not virtual parent)
      }

      currentLevel = currentLevel[part].children;
    });
  });

  return tree;
};

// Helper: Get count of untagged notes (active + archived separately)
export const getUntaggedCounts = () => {
  const notes = getNotesArray();
  let activeCount = 0;
  let archivedCount = 0;

  notes.forEach(note => {
    if (!note.tags || note.tags.length === 0) {
      if (note.archived) {
        archivedCount++;
      } else {
        activeCount++;
      }
    }
  });

  return { active: activeCount, archived: archivedCount };
};

// Create a new note
// Note: Pass userId, activeNoteId setter, and optional auto-tag from outside to avoid circular dependency
export const createNote = (title = 'Untitled Note', userId, setActiveNoteId, autoTag = null) => {
  if (!userId) {
    console.error('[Notes] Cannot create note: userId required');
    return null;
  }

  const noteId = uuidv4();
  const now = new Date().toISOString();

  const newNote = {
    id: noteId,
    title,
    tags: autoTag ? [autoTag] : [], // Auto-apply tag if provided
    note_type: 'note',
    user_id: userId,
    created_at: now,
    updated_at: now,
		deleted_at: null,
		deleted: false,
		archived: false,
		archived_at: null,
		pinned: false,
		pinned_at: null,
    source: getSource(),
  };

  notes$[noteId].set(newNote);

  // Set active note if setter provided
  if (setActiveNoteId) {
    setActiveNoteId(noteId);
  }

  // Analytics
  trackEvent('note_created', { has_auto_tag: !!autoTag });
  setOnceUserProperties({ first_note_created_at: new Date().toISOString() });
  incrementUserProperties({ total_notes_created: 1 });

  console.log('[Notes] Created note:', noteId, autoTag ? `with auto-tag: ${autoTag}` : '');
  return newNote;
};

// Update a note
export const updateNote = (noteId, updates) => {
  const note = notes$[noteId].get();
  if (!note) {
    console.error('[Notes] Note not found:', noteId);
    return;
  }

  notes$[noteId].set({
    ...note,
    ...updates,
    updated_at: new Date().toISOString(),
  });

  console.log('[Notes] Updated note:', noteId);
};

// Delete a note (soft delete)
// Note: Pass clearActiveNote callback to avoid circular dependency
export const deleteNote = (noteId, clearActiveNote) => {
  const note = notes$[noteId].get();
  if (!note) {
    console.error('[Notes] Note not found:', noteId);
    return;
  }

	const deletedAt = new Date().toISOString();
	notes$[noteId].deleted.set(true);
	notes$[noteId].deleted_at.set(deletedAt);

  // Clear active note if callback provided
  if (clearActiveNote) {
    clearActiveNote(noteId);
  }

  trackEvent('note_deleted', { note_id: noteId });

  console.log('[Notes] Deleted note:', noteId);
};

// Toggle pin - now persists to database
export const togglePin = (noteId) => {
  const note = notes$[noteId].get();
  if (!note) {
    console.error('[Notes] Note not found:', noteId);
    return;
  }

  const newPinnedState = !note.pinned;
  const now = new Date().toISOString();

  notes$[noteId].pinned.set(newPinnedState);
  notes$[noteId].pinned_at.set(newPinnedState ? now : null);
  notes$[noteId].updated_at.set(now);

  console.log('[Notes] Toggled pin:', noteId, newPinnedState);
};

// Bulk toggle pin for selected notes
export const bulkTogglePin = (noteIds, pinned) => {
  const now = new Date().toISOString();
  noteIds.forEach(noteId => {
    notes$[noteId].pinned.set(pinned);
    notes$[noteId].pinned_at.set(pinned ? now : null);
    notes$[noteId].updated_at.set(now);
  });
};

// Add tag to note (blocks smart tag prefix for manual adds)
export const addTag = (noteId, tag) => {
  if (isSmartTag(tag)) return; // Smart tags managed by AI only
  const note = notes$[noteId].get();
  if (!note) return;

  const tags = note.tags || [];
  if (!tags.includes(tag)) {
    notes$[noteId].tags.set([...tags, tag]);
    notes$[noteId].updated_at.set(new Date().toISOString());
    trackEvent('tag_added', { is_hierarchical: tag.includes('/') });
  }
};

// Remove tag from note (blocks smart tag removal)
export const removeTag = (noteId, tag) => {
  if (isSmartTag(tag)) return; // Smart tags managed by AI only
  const note = notes$[noteId].get();
  if (!note) return;

  const tags = note.tags || [];
  notes$[noteId].tags.set(tags.filter(t => t !== tag));
  notes$[noteId].updated_at.set(new Date().toISOString());
};

// Rename tag across all notes (including child tags)
export const renameTag = async (oldTag, newTag) => {
  // Check if tag is shared and confirm with user
  let sharedTags = [];
  try {
    const { getSharedTagsInHierarchy, updateSharedTagPath } = await import('./sharedTags$');
    sharedTags = getSharedTagsInHierarchy(oldTag);
    if (sharedTags.length > 0) {
      if (!confirm(`This tag is shared (${sharedTags.length} shared tag${sharedTags.length !== 1 ? 's' : ''} in hierarchy). Rename for everyone?`)) {
        return;
      }
    }
  } catch {
    // sharedTags$ not loaded yet, skip
  }

  const notes = getNotesArray();
  const now = new Date().toISOString();

  notes.forEach(note => {
    if (!note.tags) return;

    const updatedTags = note.tags.map(tag => {
      // Exact match or child tag
      if (tag === oldTag || tag.startsWith(oldTag + '/')) {
        return tag.replace(oldTag, newTag);
      }
      return tag;
    });

    if (JSON.stringify(note.tags) !== JSON.stringify(updatedTags)) {
      notes$[note.id].tags.set(updatedTags);
      notes$[note.id].updated_at.set(now);
    }
  });

  // Update shared tag paths in DB
  if (sharedTags.length > 0) {
    try {
      const { updateSharedTagPath } = await import('./sharedTags$');
      await updateSharedTagPath(oldTag, newTag);
    } catch (err) {
      console.error('[Notes] Failed to update shared tag path:', err);
    }
  }

  console.log('[Notes] Renamed tag:', oldTag, '→', newTag);
};

// Move tag under new parent (drag-drop helper)
export const moveTagToParent = (oldPath, newPath) => {
  if (oldPath === newPath) return;

  // Use existing renameTag - it handles children automatically
  renameTag(oldPath, newPath);

  console.log('[Notes] Moved tag:', oldPath, '→', newPath);
};

// Archive a note (soft archive - can be restored)
export const archiveNote = (noteId) => {
  const note = notes$[noteId].get();
  if (!note) {
    console.error('[Notes] Note not found:', noteId);
    return;
  }

  const archivedAt = new Date().toISOString();
  notes$[noteId].archived.set(true);
  notes$[noteId].archived_at.set(archivedAt);
  notes$[noteId].updated_at.set(archivedAt);

  trackEvent('note_archived', { note_id: noteId });

  console.log('[Notes] Archived note:', noteId);
};

// Unarchive a note (restore from archive)
export const unarchiveNote = (noteId) => {
  const note = notes$[noteId].get();
  if (!note) {
    console.error('[Notes] Note not found:', noteId);
    return;
  }

  notes$[noteId].archived.set(false);
  notes$[noteId].archived_at.set(null);
  notes$[noteId].updated_at.set(new Date().toISOString());

  console.log('[Notes] Unarchived note:', noteId);
};

// Restore a note from trash (undelete)
export const restoreNote = (noteId) => {
  const note = notes$[noteId].get();
  if (!note) {
    console.error('[Notes] Note not found:', noteId);
    return;
  }

  notes$[noteId].deleted.set(false);
  notes$[noteId].deleted_at.set(null);
  notes$[noteId].updated_at.set(new Date().toISOString());

  console.log('[Notes] Restored note from trash:', noteId);
};

// Permanently delete a note (hard delete - cannot be undone)
// This also permanently deletes ALL snippets (active + deleted) for this note
export const permanentlyDeleteNote = (noteId) => {
  const note = notes$[noteId].get();
  if (!note) {
    console.error('[Notes] Note not found:', noteId);
    return;
  }

  // Import snippets$ to access snippet data
  // We need to delete all snippets for this note first
  import('./snippets$').then(({ snippets$, getAllSnippetsForNote }) => {
    const noteSnippets = getAllSnippetsForNote(noteId);

    // Hard delete all snippets for this note
    noteSnippets.forEach(snippet => {
      snippets$[snippet.id].delete();
      console.log('[Notes] Permanently deleted snippet:', snippet.id);
    });

    // Then delete the note itself
    notes$[noteId].delete();
    console.log('[Notes] Permanently deleted note:', noteId, `(${noteSnippets.length} snippets)`);
  });
};

// Bulk delete selected notes
// Note: Pass selectedIds and callbacks to avoid circular dependency
export const bulkDeleteNotes = (selectedIds, clearActiveNote, clearSelection) => {
  selectedIds.forEach(noteId => deleteNote(noteId, clearActiveNote));
  if (clearSelection) {
    clearSelection();
  }
};

// Bulk archive selected notes
export const bulkArchiveNotes = (selectedIds, clearSelection) => {
  selectedIds.forEach(noteId => archiveNote(noteId));
  if (clearSelection) {
    clearSelection();
  }
};

// ============================================================================
// PIN PROTECTION FUNCTIONS
// ============================================================================

/**
 * Protect a note with PIN encryption
 * @param {string} noteId - Note ID to protect
 * @param {Object} encryptedContent - Encrypted content from pin$ store
 */
export const protectNote = (noteId, encryptedContent) => {
  const note = notes$[noteId].get();
  if (!note) {
    console.error('[Notes] Note not found:', noteId);
    return;
  }

  const now = new Date().toISOString();
  notes$[noteId].is_protected.set(true);
  notes$[noteId].encrypted_content.set(encryptedContent);
  notes$[noteId].protected_at.set(now);
  notes$[noteId].updated_at.set(now);

  trackEvent('note_protected', { note_id: noteId });

  console.log('[Notes] Note protected:', noteId);
};

/**
 * Remove protection from a note
 * @param {string} noteId - Note ID to unprotect
 */
export const unprotectNote = (noteId) => {
  const note = notes$[noteId].get();
  if (!note) {
    console.error('[Notes] Note not found:', noteId);
    return;
  }

  const now = new Date().toISOString();
  notes$[noteId].is_protected.set(false);
  notes$[noteId].encrypted_content.set(null);
  notes$[noteId].protected_at.set(null);
  notes$[noteId].updated_at.set(now);

  console.log('[Notes] Note unprotected:', noteId);
};

/**
 * Check if a note is protected
 * @param {string} noteId - Note ID to check
 * @returns {boolean}
 */
export const isNoteProtected = (noteId) => {
  const note = notes$[noteId].get();
  return note?.is_protected === true;
};

/**
 * Get encrypted content for a protected note
 * @param {string} noteId - Note ID
 * @returns {Object|null} Encrypted content object
 */
export const getEncryptedContent = (noteId) => {
  const note = notes$[noteId].get();
  return note?.encrypted_content || null;
};

/**
 * Get all protected notes
 * @returns {Array} Array of protected notes
 */
export const getProtectedNotes = () => {
  return getNotesArray().filter(note => note.is_protected === true);
};

// ── SKILL NOTES ──────────────────────────────────────────────

/** Check if a note is a skill */
export const isSkillNote = (note) => note?.note_type === 'skill';

/** Get all skill notes (active, not deleted) */
export const getSkillNotes = () => {
  return getNotesArray().filter(note => note.note_type === 'skill');
};

/** Create a new skill note with title format "skill-name/SKILL.md" */
export const createSkillNote = (skillName, userId, setActiveNoteId) => {
  if (!userId) {
    console.error('[Notes] Cannot create skill note: userId required');
    return null;
  }

  const title = `${skillName}/SKILL.md`;
  const noteId = uuidv4();
  const now = new Date().toISOString();

  const newNote = {
    id: noteId,
    title,
    tags: [],
    note_type: 'skill',
    user_id: userId,
    created_at: now,
    updated_at: now,
    deleted_at: null,
    deleted: false,
    archived: false,
    archived_at: null,
    pinned: false,
    pinned_at: null,
    source: getSource(),
  };

  notes$[noteId].set(newNote);

  if (setActiveNoteId) {
    setActiveNoteId(noteId);
  }

  trackEvent('skill_note_created', { skill_name: skillName });

  console.log('[Notes] Created skill note:', noteId, title);
  return newNote;
};

